import { useState, useCallback, useEffect, useRef } from "react";
import { NavigateFunction } from "react-router-dom";
import { toast } from "sonner";
import { ReagentFormData, UploadParams } from "@/types/reagent.types";
import { ReagentFormErrors, validateReagentForm } from "@/utils/reagentFormValidation";
import { uploadReagent } from "@/services/newReagentService";
import { dateToString } from "@/utils/dateUtils";

export const useReagentFormState = (navigate: NavigateFunction) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<ReagentFormData>({
    name: "",
    catalogNumber: "",
    category: "",
    description: "",
    storageConditions: "Room temperature",
    amount: "100",
    unit: "g",
    expiryDate: null,
    image: null,
    numberOfPackages: "",
    city: "",
    country: ""
  });
  
  const [errors, setErrors] = useState<ReagentFormErrors>({});
  const timeoutRef = useRef<number | null>(null);
  const isSubmittingRef = useRef(false);
  const formHasChangedRef = useRef(false);

  // Save form data to local storage when it changes
  useEffect(() => {
    if (formHasChangedRef.current) {
      // Don't save the image file to local storage, but save everything else
      const dataToSave = {
        ...formData,
        image: null // Don't save the image file
      };
      localStorage.setItem('reagentFormData', JSON.stringify(dataToSave));
    }
  }, [formData]);

  // Try to load saved form data on first render
  useEffect(() => {
    try {
      const savedData = localStorage.getItem('reagentFormData');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setFormData(prev => ({
          ...prev,
          ...parsedData,
          // Keep image as null since we can't serialize it to local storage
          image: null
        }));
      }
    } catch (e) {
      console.error("Error loading saved form data:", e);
    }
    
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isLoading) {
      timeoutRef.current = window.setTimeout(() => {
        setIsLoading(false);
        isSubmittingRef.current = false;
        toast.error("Upload timed out. Please try again.");
      }, 20000);
    } else if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLoading]);

  const resetUploadState = useCallback(() => {
    setIsLoading(false);
    isSubmittingRef.current = false;
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    formHasChangedRef.current = true;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    formHasChangedRef.current = true;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleDateChange = (name: string, date: Date | null) => {
    formHasChangedRef.current = true;
    setFormData(prev => ({ ...prev, [name]: date }));
    
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (file: File | null) => {
    formHasChangedRef.current = true;
    setFormData(prev => ({ ...prev, image: file }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (isLoading || isSubmittingRef.current) {
      toast.info("Upload already in progress");
      return { success: false, error: "Upload already in progress" };
    }
    
    const validation = validateReagentForm(formData);
    setErrors(validation.errors);
    
    if (!validation.isValid) {
      toast.error("Please fill in all required fields");
      return { success: false, error: "Validation failed" };
    }
    
    setIsLoading(true);
    isSubmittingRef.current = true;
    
    try {
      toast.info("Uploading reagent...");
      
      // Convert formData to UploadParams format
      const uploadParams: UploadParams = {
        name: formData.name,
        catalogNumber: formData.catalogNumber,
        category: formData.category,
        description: formData.description,
        amount: formData.amount,
        unit: formData.unit,
        storageConditions: formData.storageConditions,
        expiryDate: formData.expiryDate ? dateToString(formData.expiryDate) : null,
        image: formData.image,
        numberOfPackages: formData.numberOfPackages,
        city: formData.city,
        country: formData.country
      };
      
      const result = await uploadReagent(uploadParams);
      
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      if (result.success && result.id) {
        toast.success("Reagent uploaded successfully!");
        
        // Clear saved form data after successful upload
        localStorage.removeItem('reagentFormData');
        formHasChangedRef.current = false;
        
        setTimeout(() => {
          navigate(`/reagents/${result.id}`);
        }, 500);
        
        return result;
      } else {
        toast.error(result.error || "Failed to upload reagent");
        return result;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return {
    formData,
    errors,
    isLoading,
    handleChange,
    handleSelectChange,
    handleDateChange,
    handleFileChange,
    handleSubmit,
    resetUploadState
  };
};
